import { ChessAppLayout } from "@/components/ChessAppLayout";
import { PlayerSearchBar } from "@/components/PlayerSearchBar";
import { PlayerCard } from "@/components/PlayerCard";
import { FriendRequestsSection } from "@/components/FriendRequestsSection";
import { FriendsListSection } from "@/components/FriendsListSection";
import { usePlayerSearch } from "@/hooks/usePlayerSearch";
import { useAuth } from "@/hooks/useAuth";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Search, UserSearch, LogIn, Swords, Users, Clock, Trophy, X, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChallengeSystem } from "@/hooks/useChallengeSystem";
import { usePlayerProfiles } from "@/hooks/usePlayerProfiles";
import { supabase } from "@/integrations/supabase/client";

export default function PlayerSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { query, setQuery, results, loading } = usePlayerSearch();
  const { incomingChallenges, outgoingChallenges, acceptChallenge, declineChallenge, cancelChallenge, loading: challengeLoading } = useChallengeSystem();
  
  // Get all player IDs from challenges
  const challengePlayerIds = [
    ...incomingChallenges.map(c => c.inviter_id),
    ...outgoingChallenges.map(c => c.invitee_id)
  ];
  const { profiles } = usePlayerProfiles(challengePlayerIds);

  useSEO({
    title: "Find Chess Players - Search & Connect | ChessHatch",
    description: "Search and discover chess players on ChessHatch. Find opponents by username, ELO rating, and connect with the community.",
    keywords: "chess players search, find chess opponent, chess community, player directory"
  });

  const handleAcceptChallenge = async (invitationId: string, gameSessionId: string | null | undefined) => {
    await acceptChallenge(invitationId);
    if (gameSessionId) {
      // Update game status to ongoing
      await supabase
        .from('game_sessions')
        .update({ game_status: 'ongoing' })
        .eq('id', gameSessionId);
      
      // Navigate to game
      navigate(`/?game=${gameSessionId}`);
    }
  };

  if (!user) {
    return (
      <ChessAppLayout>
        <div className="container max-w-4xl mx-auto py-8 px-4">
          <Card>
            <CardContent className="py-12 text-center">
              <LogIn className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold mb-2">Sign in to search players</h2>
              <p className="text-muted-foreground mb-6">
                Create an account to find and challenge other players
              </p>
              <Button onClick={() => navigate('/auth')}>
                Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      </ChessAppLayout>
    );
  }

  return (
    <ChessAppLayout>
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <UserSearch className="w-8 h-8" />
            Find Chess Players
          </h1>
          <p className="text-muted-foreground">
            Search players, manage challenges, and connect with friends
          </p>
        </div>

        <Tabs defaultValue="search" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="search" className="gap-2">
              <Search className="w-4 h-4" />
              Search
            </TabsTrigger>
            <TabsTrigger value="challenges" className="gap-2">
              <Swords className="w-4 h-4" />
              Challenges
              {(incomingChallenges.length + outgoingChallenges.length) > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {incomingChallenges.length + outgoingChallenges.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="friends" className="gap-2">
              <Users className="w-4 h-4" />
              Friends
            </TabsTrigger>
          </TabsList>

          {/* Search Tab */}
          <TabsContent value="search" className="space-y-6">
            <PlayerSearchBar
              value={query}
              onChange={setQuery}
              loading={loading}
              autoFocus
            />

            {/* Loading state */}
            {loading && (
              <div className="grid gap-4 md:grid-cols-2">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Skeleton className="w-12 h-12 rounded-full" />
                        <div className="flex-1">
                          <Skeleton className="h-4 w-32 mb-2" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Results */}
            {query && !loading && results.length > 0 && (
              <>
                <div className="mb-4 text-sm text-muted-foreground">
                  Found {results.length} player{results.length !== 1 ? 's' : ''}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {results.map((player) => (
                    <PlayerCard 
                      key={player.id} 
                      player={player} 
                      showStats={true}
                      showActions={true}
                    />
                  ))}
                </div>
              </>
            )}

            {/* No results */}
            {query && !loading && results.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-xl font-semibold mb-2">No players found</h3>
                  <p className="text-muted-foreground">
                    Try searching with different terms
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Empty state - show friends when not searching */}
            {!query && !loading && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-xl font-semibold mb-2">Search for players</h3>
                  <p className="text-muted-foreground">
                    Enter a username or display name above to find opponents
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Challenges Tab */}
          <TabsContent value="challenges" className="space-y-6">
            {/* Incoming Challenges */}
            {incomingChallenges.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Swords className="w-5 h-5 text-primary" />
                    Incoming Challenges ({incomingChallenges.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {incomingChallenges.map((challenge) => {
                    const profile = profiles[challenge.inviter_id];
                    return (
                      <Card key={challenge.id} className="border-primary/20">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold truncate">
                                  {profile?.username || profile?.display_name || 'Unknown Player'}
                                </span>
                                {profile?.elo_rating && (
                                  <Badge variant="secondary" className="gap-1">
                                    <Trophy className="w-3 h-3" />
                                    {profile.elo_rating}
                                  </Badge>
                                )}
                              </div>
                              {challenge.message && (
                                <p className="text-sm text-muted-foreground mb-2">
                                  "{challenge.message}"
                                </p>
                              )}
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                {new Date(challenge.created_at).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleAcceptChallenge(challenge.id, challenge.game_session_id)}
                                disabled={challengeLoading}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => declineChallenge(challenge.id)}
                                disabled={challengeLoading}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Decline
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Outgoing Challenges */}
            {outgoingChallenges.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Swords className="w-5 h-5 text-muted-foreground" />
                    Sent Challenges ({outgoingChallenges.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {outgoingChallenges.map((challenge) => {
                    const profile = profiles[challenge.invitee_id];
                    return (
                      <Card key={challenge.id} className="border-muted">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold truncate">
                                  {profile?.username || profile?.display_name || 'Unknown Player'}
                                </span>
                                {profile?.elo_rating && (
                                  <Badge variant="secondary" className="gap-1">
                                    <Trophy className="w-3 h-3" />
                                    {profile.elo_rating}
                                  </Badge>
                                )}
                                <Badge variant="outline">Pending</Badge>
                              </div>
                              {challenge.message && (
                                <p className="text-sm text-muted-foreground mb-2">
                                  "{challenge.message}"
                                </p>
                              )}
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                {new Date(challenge.created_at).toLocaleDateString()}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => cancelChallenge(challenge.id)}
                              disabled={challengeLoading}
                            >
                              Cancel
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Empty state */}
            {incomingChallenges.length === 0 && outgoingChallenges.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Swords className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-xl font-semibold mb-2">No active challenges</h3>
                  <p className="text-muted-foreground mb-4">
                    Search for players and send them a challenge to get started
                  </p>
                  <Button onClick={() => document.querySelector<HTMLButtonElement>('[value="search"]')?.click()}>
                    <Search className="w-4 h-4 mr-2" />
                    Search Players
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Friends Tab */}
          <TabsContent value="friends" className="space-y-6">
            <FriendRequestsSection />
            <FriendsListSection />
          </TabsContent>
        </Tabs>
      </div>
    </ChessAppLayout>
  );
}
